import React from 'react';
import PropTypes from 'prop-types';
import { FullWidthPaperContent } from 'components/Paper';
import styled from 'styled-components';
import WidgetContainer from 'components/widget/WidgetContainer';
import UsageAndPrice from 'components/UsageAndPrice';
import Button from 'components/Button';
import moment from 'moment';
import InfoButton from 'components/InfoButton';
import getBoxSizeString from 'components/helpers/getBoxSizeString';
import { useHistory } from 'react-router';
import changeLensAmountContainerPath from 'containers/paths/ChangeLensAmountContainerPath';
import BodyText from 'components/widget/BodyText';
import ProductImageView from 'components/ProductImageView';
import NextPaymentDifferent from 'components/infoBoxes/NextPaymentDifferent';
import WillSwitchUsage from 'components/infoBoxes/WillSwitchUsage';

const SUBSCRIPTION_EXPIRY_TOOLTIP = (
  <p>
    When your subscription expires you&apos;ll need to come in and see us to
    continue receiving your lenses.
  </p>
);

const StyledPackageWidgetDetails = styled(WidgetContainer)`
  .subscriptionInfo {
    margin-top: ${({ theme }) => theme.spacing * 2}px;
    padding: ${({ theme }) => theme.spacing * 2}px
      ${({ theme }) => theme.spacing * 4}px;
    display: flex;
    justify-content: space-between;
    background-color: ${({ theme }) => theme.palette.grey[4]};
    position: relative;
    ${InfoButton} {
      margin-left: 0.5em;
    }

    .subscriptionExpiry {
      text-align: right;
    }
  }
`;

const YourPackageDetailsWidget = ({
  className,
  pack,
  changeUsageDisableReason,
  memberSince,
  subscriptionExpiry,
  nextPaymentAmount,
  hideChangeUsage,
}) => {
  const classes = ['YourPackageDetailsWidget', className];
  const { product } = pack;
  const products = Object.values(
    product.items.reduce((accumulator, next) => {
      accumulator[next.styleNumber] = next;
      return accumulator;
    }, {}),
  );
  const history = useHistory();

  function onChangeLensAmount() {
    history.push(changeLensAmountContainerPath());
  }
  const payment = pack.payments.find((p) => p.amount > 0);
  const price = {
    price: payment.amount,
    currency: payment.currency,
    frequency: 'mo.',
  };

  /**
   * Renders an infoBox if necessary.
   * Only one info box will be returned, therefore, if there are packChanges, the outstanding
   * amount message will not be displayed
   *
   * @returns {null|*}
   */
  const renderInfoBox = () => {
    if (pack && pack.packChanges) {
      return (
        <WillSwitchUsage
          currentProduct={pack.product}
          newProduct={pack.packChanges.product}
          deliveryDate={pack.deliveryDate}
        />
      );
    }

    if (nextPaymentAmount && nextPaymentAmount !== price.price) {
      return (
        <NextPaymentDifferent currency={price.currency} nextPaymentAmount={nextPaymentAmount} />
      );
    }

    return null;
  };

  const widgetRef = React.useRef();

  return (
    <StyledPackageWidgetDetails className={classes.filter((c) => !!c).join(' ')}>
      <h4>Your package</h4>
      <UsageAndPrice usage={products[0].type} price={price} />
      <BodyText>{getBoxSizeString(product.packSize, product.hasSolution)}</BodyText>

      <ProductImageView items={product.items} format="thumbnailAndText" />

      {renderInfoBox()}

      {!hideChangeUsage && (
      <Button
        fullWidth
        disableReason={changeUsageDisableReason}
        onClick={onChangeLensAmount}
        dataLayer={{ action: 'Change Lens Amount', label: 'Change Lens Amount - Start' }}
      >
        Change package
      </Button>
      )}

      <FullWidthPaperContent className="subscriptionInfo" ref={widgetRef}>
        <div>
          {memberSince && (
            <>
              <h4>Member since</h4>
              <p>{moment(memberSince).format('MMMM Do YYYY')}</p>
            </>
          )}
        </div>
        <div className="subscriptionExpiry">
          {subscriptionExpiry && (
            <>
              <h4>Subscription expiry</h4>
              <div>
                {moment(subscriptionExpiry).format('MMMM Do YYYY')}
                <InfoButton overlapElementRef={widgetRef}>{SUBSCRIPTION_EXPIRY_TOOLTIP}</InfoButton>
              </div>
            </>
          )}
        </div>
      </FullWidthPaperContent>
    </StyledPackageWidgetDetails>
  );
};

YourPackageDetailsWidget.displayName = 'YourPackageDetailsWidget';

YourPackageDetailsWidget.propTypes = {
  className: PropTypes.string,
  changeUsageDisableReason: PropTypes.string,
  pack: PropTypes.shape({
    payments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    deliveryDate: PropTypes.string,
    product: PropTypes.shape({
      hasSolution: PropTypes.bool,
      packSize: PropTypes.shape({
        boxSize: PropTypes.number.isRequired,
        usage: PropTypes.string.isRequired,
      }).isRequired,
      items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    }).isRequired,
    packChanges: PropTypes.shape({
      product: PropTypes.shape({
        packSize: PropTypes.shape({
          boxSize: PropTypes.number.isRequired,
          usage: PropTypes.string.isRequired,
        }).isRequired,
      }),
    }),
  }).isRequired,
  memberSince: PropTypes.string,
  subscriptionExpiry: PropTypes.string,
  nextPaymentAmount: PropTypes.number,
  hideChangeUsage: PropTypes.bool,
};

YourPackageDetailsWidget.defaultProps = {
  className: null,
  changeUsageDisableReason: null,
  memberSince: null,
  subscriptionExpiry: null,
  nextPaymentAmount: null,
  hideChangeUsage: false,
};

export default YourPackageDetailsWidget;
